// GHOSTS — every blank the OS can see, and what it offers for each.
//
// CONSUMER: shared (the web prep screen + node-pool tests).
//
// Grant, 2026-09-29, with a screenshot of the prep screen showing 191
// fields, "Accept all" or "Clear suggestions", and "107 locked boxes
// have nothing typed in them yet": "in most situations i do not need
// all or none. i need a few."
//
// THE RULE (docs/design/esign-prepare-and-send-redesign.md)
//
//   Every blank the OS can see is drawn as a GHOST, and a ghost costs
//   nothing. Three acts by the sender make it real: accept the value the
//   OS offers, type a value, or hand it to a signer. Anything untouched
//   is not sent, and the send says how many. Signing marks (signature,
//   initials, date signed, the printed name beside them) are never
//   ghosts: they are placed for real on open, because they are what the
//   document is being sent for.
//
// On the prep screen a ghost is a field still carrying its `proposal`
// marker. That marker existed before this rule (every rung of the
// suggestion ladder sets it), so nothing here changes how a suggestion
// is made — only what one MEANS: until 2026-09-29 the send payload
// carried every proposal (Send was an implicit accept), and "Accept all"
// turned every one into a box that could then block the send for being
// empty. Now the marker is the line between "drawn" and "sent".
//
// This module answers two questions the screen asks about a ghost, both
// pure so they can be tested without React:
//
//   1. WHAT DOES THE OS OFFER FOR IT?  ghostOffer() — a value with a
//      named source, or nothing. The source is what the sender sees as
//      a tag on the page, and it decides whether a bulk accept may take
//      the value without a person looking (Grant, 2026-09-29: "go with
//      your recommendations" on the drawing's question 1).
//   2. WHAT GOES OUT?  partitionForSend() — the real fields, and the
//      ghosts left behind, counted from the same array so the number the
//      send line reads can never describe a different population than
//      the payload (CLAUDE.md, "Accuracy").

import { isSigningBlockField } from "./vector-blanks";
import { parseFillRule } from "./fill-rules";
import { deriveRuleIsImplemented } from "./derive-rules";
import type { EsignFieldType } from "./field-types";
import { isPreparerField } from "./field-types";
import type { SuggestionSource } from "./field-suggestions";

/** Where an offered value comes from. Shown to the sender verbatim as
 *  the tag on the ghost, in these words:
 *
 *    record     a column on the record the document is sent from
 *    contact    the person in the seat this box belongs to
 *    feed       an outside source read at package start (the fill rule
 *               names it: the ARMLS sheet, a rate sheet)
 *    default    the firm's standard answer, governed in one place
 *    last_send  what this form's saved map carried from the last send
 *    handed     the map remembers this box as a SIGNER's to fill in,
 *               with nothing typed — "for Jodie", not a value
 *    document   Stew read it off ANOTHER document in the package (a
 *               pre-approval letter); tagged with that document's name
 *    stew       Stew read it off the words beside the blank
 *
 *  Only `document` and `stew` are guesses. Everything else is a fact the OS holds or a
 *  choice a person already made, which is why the sure ones may be
 *  taken in bulk and Stew's may not. */
export type OfferKind =
  | "record"
  | "contact"
  | "feed"
  | "default"
  | "last_send"
  | "handed"
  | "document"
  | "stew";

export interface GhostOffer {
  kind: OfferKind;
  /** For a `document` offer: the name of the document in the package
   *  the value was read off ("Joey's pre-qual letter"), which is the
   *  tag the sender sees. */
  sourceName?: string;
  /** The text that would print, when the offer is a value. Null for a
   *  `handed` offer (nothing prints; the signer types it) and for a
   *  value the OS will work out at send time (an implemented derive
   *  rule: the addenda boxes tick from what is in the envelope). */
  value: string | null;
  /** May a bulk accept take this without a person looking? */
  sure: boolean;
}

/** The shape this module reads. Structural, so the prep screen's field
 *  and a test fixture both satisfy it without a React tree. */
export interface GhostLike {
  signerKey: string;
  fieldType: EsignFieldType;
  value?: string | null;
  mergeToken?: string | null;
  fillRule?: string | null;
  proposal?: { source: SuggestionSource } | undefined;
  /** What Stew read for this blank off ANOTHER document in the package
   *  (2026-09-30, the drawing's eighth source). Attached only to a
   *  blank nothing else answered; offered as a guess, never sure. */
  reading?: { value: string; documentName: string } | null;
}

/** Is this field still a ghost — drawn, not yet real? */
export function isGhost(f: { proposal?: unknown }): boolean {
  return Boolean(f.proposal);
}

const CONTACT_TOKEN = /^contact\./;

/**
 * What the OS offers for a ghost, or null when it offers nothing.
 *
 * `resolvedValue` is what the ghost's binding resolves to on THIS send
 * (the screen's own merge preview), or null when it is unbound or the
 * record has nothing. Asked for rather than computed here because the
 * resolution depends on who holds which seat, which the screen knows
 * and this module deliberately does not.
 *
 * Cheapest and most certain first, matching the fill-rule vocabulary
 * (fill-rules.ts): a governed default and a named calculation outrank a
 * binding, and a binding outranks what the map happened to carry.
 */
export function ghostOffer(f: GhostLike, resolvedValue: string | null): GhostOffer | null {
  if (!f.proposal) return null;
  // A signing mark is never a ghost; if one arrives still marked, it
  // offers nothing rather than pretending to be a value.
  if (isSigningBlockField(f)) return null;

  const typed = (f.value ?? "").trim();
  const bound = (f.mergeToken ?? "").trim().length > 0;
  const resolved = (resolvedValue ?? "").trim();
  const rule = parseFillRule(f.fillRule);
  // What another document in the package says, offered only where
  // nothing surer answers: every `return null` below that means "the
  // OS has nothing to say" says this instead when a reading exists.
  const reading: GhostOffer | null = f.reading?.value?.trim()
    ? {
        kind: "document",
        value: f.reading.value.trim(),
        sure: false,
        sourceName: f.reading.documentName,
      }
    : null;

  // Stew's reading is a guess whatever it resolved to. Held for a look.
  if (f.proposal.source === "stew") {
    const value = bound ? resolved || null : typed || null;
    return value ? { kind: "stew", value, sure: false } : null;
  }

  if (rule?.kind === "default" && typed) {
    return { kind: "default", value: typed, sure: true };
  }
  if (rule?.kind === "derive" && deriveRuleIsImplemented(rule.ref)) {
    // Worked out at send time from the envelope itself (the addenda
    // boxes). Nothing to show yet, and nothing for a person to check.
    return { kind: "record", value: null, sure: true };
  }
  if (bound) {
    // A binding with nothing behind it is not an offer: the box would
    // print nothing, so the sender is not asked to accept nothing. It
    // stays a ghost, counted with the rest, and the panel says how many
    // linked blanks the record could not answer — unless another
    // document in the package answered it.
    if (!resolved) return reading;
    const kind: OfferKind =
      rule?.kind === "feed"
        ? "feed"
        : CONTACT_TOKEN.test(f.mergeToken ?? "")
          ? "contact"
          : "record";
    return { kind, value: resolved, sure: true };
  }
  if (f.proposal.source === "saved_layout") {
    // The map carried text last time: the firm's boilerplate, or a value
    // that showed up on two independent sends (layout-apply.ts, S12).
    if (typed) return { kind: "last_send", value: typed, sure: true };
    // The map remembers this box as a signer's to fill in. Offering it
    // is what keeps a mapped Mortgage package one press: every box
    // Baker laid out for the borrower comes back as "for the borrower"
    // rather than as a blank the sender must hand over again.
    if (!isPreparerField(f)) return { kind: "handed", value: null, sure: true };
    return reading;
  }
  // A free rung (a ruled line, a form widget, a caption) with nothing
  // behind it: the OS saw the blank and has nothing to say about it.
  if (typed) return { kind: "last_send", value: typed, sure: true };
  return reading;
}

/** The tag on the page for an offer: the source word, or for a value
 *  read off another document, that document's name. */
export function offerTag(offer: GhostOffer): string {
  if (offer.kind === "document" && offer.sourceName) return `from ${offer.sourceName}`;
  return OFFER_KIND_LABEL[offer.kind];
}

/** The plain word for a source, as the page tag and the panel row read. */
export const OFFER_KIND_LABEL: Readonly<Record<OfferKind, string>> = {
  record: "record",
  contact: "contact",
  feed: "feed",
  default: "firm default",
  last_send: "last send",
  handed: "for the signer",
  document: "from another document",
  stew: "Stew read it",
};

/** The panel's group heading for a source. */
export const OFFER_KIND_HEADING: Readonly<Record<OfferKind, string>> = {
  record: "This record",
  contact: "The people in the seats",
  feed: "An outside source",
  default: "Firm defaults",
  last_send: "Your last send of this form",
  handed: "Boxes the map gives to a signer",
  document: "Another document in the package",
  stew: "Stew read it off the page",
};

/** Group order in the panel: sure and common first, the guess last. */
export const OFFER_KIND_ORDER: readonly OfferKind[] = [
  "record",
  "contact",
  "feed",
  "default",
  "last_send",
  "handed",
  "document",
  "stew",
];

/** How the count strip reads a document: every number over the same
 *  list, so they add up to `total` by construction. */
export interface GhostCensus {
  /** Every field the screen holds, real and ghost. */
  total: number;
  /** Real signing marks: signature, initials, date signed, printed name. */
  signing: number;
  /** Every other real field: accepted, typed, handed, or placed by hand. */
  real: number;
  /** Ghosts with an offer a bulk accept may take. */
  offeredSure: number;
  /** Ghosts with an offer that wants a look first (Stew's). */
  offeredReview: number;
  /** Ghosts the fill rule says only the sender can answer, with no
   *  offer. The "only you" number. */
  asked: number;
  /** Ghosts with no offer and no question: ignored unless touched. */
  ignored: number;
}

export function ghostCensus<T extends GhostLike>(
  fields: readonly T[],
  offerOf: (f: T) => GhostOffer | null,
  /** Is this ghost one the sender is being asked about (the only-you
   *  list's `ask` / `gap` kinds)? */
  isAsked: (f: T) => boolean,
): GhostCensus {
  const census: GhostCensus = {
    total: fields.length,
    signing: 0,
    real: 0,
    offeredSure: 0,
    offeredReview: 0,
    asked: 0,
    ignored: 0,
  };
  for (const f of fields) {
    if (!isGhost(f)) {
      if (isSigningBlockField(f)) census.signing += 1;
      else census.real += 1;
      continue;
    }
    const offer = offerOf(f);
    if (offer) {
      if (offer.sure) census.offeredSure += 1;
      else census.offeredReview += 1;
    } else if (isAsked(f)) {
      census.asked += 1;
    } else {
      census.ignored += 1;
    }
  }
  return census;
}

/** What goes out and what stays behind. One split, used by the payload
 *  builder and by the send line, so "Sending 149 fields · 76 blanks
 *  left empty" is counted off exactly the arrays that travel. */
export function partitionForSend<T extends { proposal?: unknown }>(
  fields: readonly T[],
): { sent: T[]; ghosts: T[] } {
  const sent: T[] = [];
  const ghosts: T[] = [];
  for (const f of fields) (isGhost(f) ? ghosts : sent).push(f);
  return { sent, ghosts };
}

/** The package's census: every document's numbers added up, so the
 *  panel's count strip and "Accept all N ready" describe the whole
 *  envelope when Accept all is package-wide (Grant's decision 2,
 *  2026-09-29). Summing keeps the accuracy rule's guarantee: each part
 *  was counted over its own document's full list, so the total is
 *  counted over the package's. */
export function sumCensus(parts: readonly GhostCensus[]): GhostCensus {
  const out: GhostCensus = {
    total: 0,
    signing: 0,
    real: 0,
    offeredSure: 0,
    offeredReview: 0,
    asked: 0,
    ignored: 0,
  };
  for (const p of parts) {
    out.total += p.total;
    out.signing += p.signing;
    out.real += p.real;
    out.offeredSure += p.offeredSure;
    out.offeredReview += p.offeredReview;
    out.asked += p.asked;
    out.ignored += p.ignored;
  }
  return out;
}
